import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'

interface FavoriteHeartButtonProps {
  routeId: string
  initial?: boolean
  /** When true, render a borderless / backgroundless heart that lives inline
   * with its sibling content (e.g. centred on a page title) instead of the
   * default chip-style button used in route cards. */
  bare?: boolean
}

export function FavoriteHeartButton({
  routeId,
  initial = false,
  bare = false,
}: FavoriteHeartButtonProps) {
  const auth = useAuth()
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const [favorite, setFavorite] = useState(initial)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    setFavorite(initial)
  }, [initial, routeId])

  async function toggle() {
    if (auth.status !== 'authed' || !auth.token) {
      navigate('/signin')
      return
    }
    if (pending) return

    const next = !favorite
    setFavorite(next)
    setPending(true)
    try {
      if (next) {
        await api.createFavorite(routeId, auth.token)
      } else {
        await api.deleteFavorite(routeId, auth.token)
      }
    } catch {
      setFavorite(!next)
    } finally {
      setPending(false)
    }
  }

  const label = favorite ? t('aria.removeFromFavorites') : t('aria.addToFavorites')
  const cls = bare
    ? favorite
      ? 'text-[var(--xr-danger)] hover:text-[var(--xr-danger)]'
      : 'text-[var(--xr-text-muted)] hover:text-[var(--xr-danger)]'
    : favorite
      ? 'border-[var(--xr-danger)] bg-[var(--xr-danger-soft)] text-[var(--xr-danger)] hover:bg-[var(--xr-danger-soft)]'
      : 'border-[var(--xr-border-strong)] bg-[var(--xr-bg-surface)] text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] hover:text-[var(--xr-danger)]'
  const chrome = bare
    ? 'inline-flex h-7 w-7 flex-none items-center justify-center rounded-[var(--xr-radius-sm)] transition disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--xr-brand-primary)]'
    : 'inline-flex h-8 w-8 flex-none items-center justify-center rounded-[var(--xr-radius-full)] border shadow-[var(--xr-shadow-sm)] transition disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--xr-brand-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--xr-bg-canvas)]'

  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={favorite}
      title={label}
      disabled={pending}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void toggle()
      }}
      className={`${chrome} ${cls}`}
    >
      <svg
        viewBox="0 0 24 24"
        width={bare ? '20' : '16'}
        height={bare ? '20' : '16'}
        fill={favorite ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
      </svg>
    </button>
  )
}
